import React from "react";
import { Helmet } from "react-helmet-async";
import { useSettings } from "../lib/settings";

/**
 * Reusable SEO head injector.
 * Usage: <SEOHead slug="home" />  OR  <SEOHead title="X" description="Y" jsonLd={[...]} />
 * Falls back to global settings + admin-managed page SEO.
 */
export default function SEOHead({ slug, title, description, image, keywords, noIndex, type = "website", jsonLd }) {
  const { settings, pageSeoMap } = useSettings();
  if (!settings) return null;

  const page = slug ? pageSeoMap[slug] : null;
  const finalTitle = title || page?.title || settings.default_meta_title;
  const finalDesc = description || page?.description || settings.default_meta_description;
  const finalKeywords = keywords || page?.keywords || settings.default_meta_keywords;
  const finalImage = image || page?.og_image || settings.og_image;
  const base = (settings.canonical_base_url || "").replace(/\/$/, "");
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const canonical = page?.canonical || `${base}${path}`;
  const robots = noIndex || page?.no_index ? "noindex,nofollow" : "index,follow";

  const absImage = finalImage?.startsWith("http") ? finalImage : `${base}${finalImage || ""}`;

  const orgSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    name: settings.site_title,
    description: settings.default_meta_description,
    url: base,
    logo: settings.favicon_url?.startsWith("http") ? settings.favicon_url : `${base}${settings.favicon_url || ""}`,
    telephone: settings.contact_phone,
    email: settings.contact_email,
    address: { "@type": "PostalAddress", streetAddress: settings.address, addressCountry: "IN" },
    sameAs: [settings.social_facebook, settings.social_instagram, settings.social_linkedin, settings.social_youtube].filter(Boolean),
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: settings.site_title,
    url: base,
  };

  const extra = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <html lang="en-IN" />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      {finalKeywords && <meta name="keywords" content={finalKeywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      {settings.favicon_url && <link rel="icon" href={settings.favicon_url} />}
      {settings.google_search_console_verification && (
        <meta name="google-site-verification" content={settings.google_search_console_verification} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content={settings.site_title} />
      {absImage && <meta property="og:image" content={absImage} />}
      {absImage && <meta property="og:image:width" content="1200" />}
      {absImage && <meta property="og:image:height" content="630" />}
      {absImage && <meta property="og:image:alt" content={finalTitle} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      {absImage && <meta name="twitter:image" content={absImage} />}

      {/* Structured data */}
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      {extra.map((obj, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(obj)}</script>
      ))}
    </Helmet>
  );
}
